"use client"

import { downloadContentItems, extFromUrl, type DownloadItem, type ContentDownloadT } from "@/lib/content-download"
import type { CollectionItemDetail } from "@/components/goonfolio/types"

function sanitizeFilenamePart(s: string): string {
  const cleaned = s.replace(/[\\/:*?"<>|]+/g, "").trim().slice(0, 60)
  return cleaned || "item"
}

/**
 * One entry per booru source that saves a "placeholder" snapshot instead of
 * its full gallery at add-time (BooruDetailModal's own lazy-load effects
 * cover the same sources — see its needsDetailFetch/galleryUrl effects).
 * Adding support for a new source here is exactly one entry, not a new
 * if-block buried in resolveGoonfolioItemDownloads — found via this exact
 * task that XlecX had this pattern too and was missing from the first pass,
 * which is the risk a growing if-chain runs into.
 */
interface LazyGallerySource {
  matches: (snapshot: Record<string, any>) => boolean
  requestUrl: (snapshot: Record<string, any>) => string
  extractUrls: (data: any) => string[]
}

const LAZY_GALLERY_SOURCES: LazyGallerySource[] = [
  {
    // Erome placeholder posts (eager fetch hit a size limit / failed).
    matches: (s) => s.site === "erome" && !!s.rawPost?.needsDetailFetch && !!s.source,
    requestUrl: (s) => `/api/booru/erome/gallery?url=${encodeURIComponent(s.source)}`,
    extractUrls: (data) => (data.pages || []).map((p: { url: string }) => p.url).filter(Boolean),
  },
  {
    // Doujins posts saved with just a gallery link, no page list yet.
    matches: (s) => s.site === "doujins" && !!s.rawPost?.galleryUrl,
    requestUrl: (s) => `/api/booru/doujins/gallery?url=${encodeURIComponent(s.rawPost.galleryUrl)}`,
    extractUrls: (data) => (data.pages || []).map((p: { imageUrl: string }) => p.imageUrl).filter(Boolean),
  },
  {
    // XlecX posts — postUrl-based lazy fetch, covers both album pages and video posts.
    matches: (s) => s.site === "xlecx" && !!s.rawPost?.needsDetailFetch && !!s.postUrl,
    requestUrl: (s) => `/api/booru/xlecx/gallery?url=${encodeURIComponent(s.postUrl)}`,
    extractUrls: (data) => (data.pages || []).map((p: { imageUrl: string }) => p.imageUrl).filter(Boolean),
  },
]

async function resolveLazyAlbumImages(snapshot: Record<string, any>): Promise<string[]> {
  const source = LAZY_GALLERY_SOURCES.find((s) => s.matches(snapshot))
  if (!source) return []
  try {
    const res = await fetch(source.requestUrl(snapshot))
    if (!res.ok) return []
    return source.extractUrls(await res.json())
  } catch {
    return []
  }
}

/**
 * Resolves ONE saved item to its full set of downloadable files. Mirrors the
 * exact lazy-load logic BooruDetailModal already uses when opening a
 * placeholder post (see its rawPost?.needsDetailFetch / galleryUrl effects) —
 * some saved external_content snapshots only stored a single cover image at
 * save-time (Erome galleries fetched eagerly can hit a size limit and fall
 * back to a placeholder; Doujins posts can be saved with just galleryUrl and
 * no albumImages yet). Without re-resolving those here first, a "download
 * this item" would silently grab just the cover instead of the whole
 * album/gallery, which is exactly the gap this function closes.
 */
export async function resolveGoonfolioItemDownloads(
  item: CollectionItemDetail,
  index: number,
): Promise<DownloadItem[]> {
  if (item.item_type === "script") {
    const script = item.community_scripts
    const title = sanitizeFilenamePart(script?.title || `script-${index + 1}`)
    const out: DownloadItem[] = []
    if (script?.thumbnail_url) {
      out.push({ url: script.thumbnail_url, filename: `${title}-thumbnail.${extFromUrl(script.thumbnail_url)}` })
    }
    if (script?.preview_video_url) {
      out.push({ url: script.preview_video_url, filename: `${title}-preview.${extFromUrl(script.preview_video_url)}` })
    }
    return out
  }

  const snapshot = item.external_content || {}
  const baseTitle = sanitizeFilenamePart(snapshot.title || snapshot.albumTitle || `item-${index + 1}`)

  let albumImages: string[] = Array.isArray(snapshot.albumImages)
    ? snapshot.albumImages.filter((u: unknown): u is string => typeof u === "string" && u.trim().length > 0)
    : []

  // Re-fetch the real gallery for a placeholder snapshot instead of
  // downloading just the cover/thumbnail that got stored at save-time —
  // see LAZY_GALLERY_SOURCES above for which sources need this.
  if (albumImages.length === 0) {
    albumImages = await resolveLazyAlbumImages(snapshot)
  }

  if (albumImages.length > 1) {
    return albumImages.map((url, i) => ({ url, filename: `${baseTitle}-${i + 1}.${extFromUrl(url)}` }))
  }

  const url = albumImages[0] || snapshot.fileUrl || snapshot.sampleUrl || snapshot.previewUrl
  if (!url) return []
  return [{ url, filename: `${baseTitle}.${extFromUrl(url)}` }]
}

/** Downloads a single Goonfolio item — one file downloads directly, an
 * album/multi-file item auto-zips (same rule as downloadContentItems). */
export async function downloadGoonfolioItem(
  item: CollectionItemDetail,
  index: number,
  zipName: string,
  t?: ContentDownloadT,
): Promise<boolean> {
  const files = await resolveGoonfolioItemDownloads(item, index)
  return downloadContentItems(files, zipName, { t })
}

/** Downloads an entire folio — every item's files (thumbnails + videos for
 * scripts, every album page for a gallery, the single file for a plain
 * post) flattened into one zip. */
export async function downloadGoonfolio(
  items: CollectionItemDetail[],
  zipName: string,
  onProgress?: (percent: number) => void,
  t?: ContentDownloadT,
): Promise<boolean> {
  const resolved: DownloadItem[] = []
  for (let i = 0; i < items.length; i++) {
    resolved.push(...(await resolveGoonfolioItemDownloads(items[i], i)))
    onProgress?.(Math.round(((i + 1) / items.length) * 20))
  }
  // De-dupe filenames across items (two items could produce the same
  // sanitized title, e.g. two untitled posts both falling back to
  // "item-N" — no, N differs per index so those never collide — but two
  // real posts could share the same title) by suffixing a running index.
  const seen = new Map<string, number>()
  const named = resolved.map((f) => {
    const count = seen.get(f.filename) ?? 0
    seen.set(f.filename, count + 1)
    if (count === 0) return f
    const dot = f.filename.lastIndexOf(".")
    return dot > 0
      ? { ...f, filename: `${f.filename.slice(0, dot)}-${count + 1}${f.filename.slice(dot)}` }
      : { ...f, filename: `${f.filename}-${count + 1}` }
  })
  return downloadContentItems(named, zipName, { onProgress: (p) => onProgress?.(20 + Math.round(p * 0.8)), t })
}
